import React, { useEffect, useState } from 'react';
import StatCard from './components/StatCard/StatCard';
import { FaShoppingCart, FaBox, FaBoxOpen, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';
import styles from './dashboard.module.scss';
import { Dashboard as DashboardModel } from '../../../models/Dashboard/Dashboard';
import { DashboardResponse } from '../../../models/Dashboard/contracts';
import { Category } from '../../../models/Category/Category';
import OrdersByCategoryChart from './components/Charts/OrdersByCategoryChart';
import RevenueOverTimeChart from './components/Charts/RevenueOverTimeChart';
import UpcomingEventsTable from './components/UpcomingEvents/UpcomingEventsTable';
import ActivityLogTable from './components/ActivityLogTable/ActivityLogTable';
import EventsSwitcher from './components/UpcomingEvents/EventsSwitcher';
import QuickActionsRow from './components/QuickActionRow/QuickActionsRow';
import { startOfMonth, endOfMonth, subDays } from 'date-fns';

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [ordersByCategory, setOrdersByCategory] = useState<any[]>([]);
  const [revenueOverTime, setRevenueOverTime] = useState<any[]>([]);
  const [categoriesCount, setCategoriesCount] = useState<number>(0);
  const [galleryCount, setGalleryCount] = useState<number>(0);
  const [quickCounts, setQuickCounts] = useState<{products: number, categories: number, orders: number, media: number} | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // This period state will now control the whole dashboard, including the chart
  const [period, setPeriod] = useState<{start: Date, end: Date}>({
    start: subDays(new Date(), 30), // Default to last 30 days
    end: new Date()
  });
  
  // This state will track the active selection in the dropdown (e.g., 7, 30, 90)
  const [selectedPeriodDays, setSelectedPeriodDays] = useState<number>(30);


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [metricsResponse, ordersResponse, revenueResponse, categories, counts] = await Promise.all([
          DashboardModel.getMetrics(),
          DashboardModel.getOrdersByCategory(period.start, period.end),
          DashboardModel.getRevenueOverTime(period.start, period.end),
          Category.all(),
          DashboardModel.getCounts()
        ]);

        setData(metricsResponse);
        setOrdersByCategory(ordersResponse);
        setRevenueOverTime(revenueResponse);
        setCategoriesCount(countCategories(categories));
        setGalleryCount(counts.media);
        setQuickCounts(counts);
      } catch (err) {
        setError('Greška pri učitavanju podataka');
        console.error('Dashboard data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period]); // The useEffect now correctly depends on the period state

  // This function will be passed down to the chart component
  const handlePeriodChange = (days: number) => {
    setSelectedPeriodDays(days); // Update the selected dropdown value
    const endDate = new Date();
    // If 'All Time' is selected, you might need a different logic,
    // for example, fetching from the project's start date or a very old date.
    // For now, let's handle a large range for "All time" (value 0).
    const startDate = days === 0 ? new Date('2020-01-01') : subDays(endDate, days);
    
    setPeriod({ start: startDate, end: endDate });
  };


  function countCategories(categories: Category[]): number {
    let count = 0;
    for (const cat of categories) {
      count += 1;
      if (cat.children && cat.children.length > 0) {
        count += countCategories(cat.children);
      }
    }
    return count;
  }

  if (loading) {
    return <div className={styles.dashboard}>Učitavanje...</div>;
  }

  if (error) {
    return <div className={styles.dashboard}>{error}</div>;
  }

  if (!data) {
    return <div className={styles.dashboard}>Nema dostupnih podataka</div>;
  }

  const { metrics, currency } = data.data;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const barData = ordersByCategory.map(item => ({
    category: item.kategorija,
    orders: item.narudzbine
  }));

  const lineData = revenueOverTime.map(item => ({
    // Ensure the month/date format from API is what you expect for the XAxis
    date: item.datum || item.mesec, // Use a generic 'date' key
    revenue: item.prihod
  }));

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1>Kontrolna tabla</h1>
        {/* The display period is now dynamic */}
        <div className={styles.period}>
          {period.start.toLocaleDateString()} – {period.end.toLocaleDateString()}
        </div>
      </div>
      
      <div className={styles.statsGrid}>
        {/* StatCards remain the same */}
        <StatCard
          title="Ukupno narudžbina"
          value={metrics.total_orders.value.toLocaleString()}
          icon={<FaShoppingCart />}
          trend={{ value: metrics.total_orders.trend, isPositive: metrics.total_orders.trend >= 0 }}
          info="u odnosu na prošli mesec"
        />
        <StatCard
          title="Dostupni proizvodi"
          value={metrics.available_products.value.toLocaleString()}
          icon={<FaBox />}
          trend={{ value: metrics.available_products.trend, isPositive: metrics.available_products.trend >= 0 }}
          info="u odnosu na prošli mesec"
        />
        <StatCard
          title="Iznajmljeni proizvodi"
          value={metrics.rented_products.value.toLocaleString()}
          icon={<FaBoxOpen />}
          trend={{ value: metrics.rented_products.trend, isPositive: metrics.rented_products.trend >= 0 }}
          info="u odnosu na prošli mesec"
        />
        <StatCard
          title="Rezervisani proizvodi"
          value={metrics.reserved_products.value.toLocaleString()}
          icon={<FaCalendarAlt />}
          trend={{ value: metrics.reserved_products.trend, isPositive: metrics.reserved_products.trend >= 0 }}
          info="u odnosu na prošli mesec"
        />
        <StatCard
          title="Ukupan prihod"
          value={formatCurrency(metrics.total_revenue.value)}
          icon={<FaMoneyBillWave />}
          trend={{ value: metrics.total_revenue.trend, isPositive: metrics.total_revenue.trend >= 0 }}
          info="u odnosu na prošli mesec"
        />
      </div>

      <div className={styles.chartsGrid}>
        <OrdersByCategoryChart data={barData} />
        {/* Pass the new state and handler function down to the chart */}
        <RevenueOverTimeChart 
          data={lineData} 
          currency={currency}
          onPeriodChange={handlePeriodChange}
          selectedPeriod={selectedPeriodDays}
        />
      </div>
      
      {/* Rest of the dashboard remains the same */}
      <div className={styles.eventsSwitcherRow}>
        <EventsSwitcher />
      </div>

      <div className={styles.activityLogRow}>
        <ActivityLogTable />
      </div>

      <div className={styles.quickActionsRow}>
        <QuickActionsRow
          productsCount={quickCounts ? quickCounts.products : metrics.available_products.value}
          categoriesCount={quickCounts ? quickCounts.categories : categoriesCount}
          ordersCount={quickCounts ? quickCounts.orders : metrics.total_orders.value}
          galleryCount={quickCounts ? quickCounts.media : galleryCount}
        />
      </div>
    </div>
  );
};

export default Dashboard;
